import { createApp, h } from 'vue';
import VModal from './v-modal.vue';
import ModalInfo from './modal-info.vue';
import ModalConfirm from './modal-confirm.vue';
import ModalDictionary from './modal-dictionary.vue';
import ModalNotification from './modal-notification.vue';
import tolgee from '@/tolgee.js';

const close = function (parentEl, app) {
  parentEl?.parentNode?.removeChild(parentEl);
  app.unmount();
  app = null;
};

const popup = function (component, data, onClosed) {
  const el = document.createElement('div');
  el.classList.add('v-modal__slot');
  document.body.appendChild(el);
  // `h(component, props)` rather than `createApp({ extends: component }, props)`:
  // `extends` cannot inherit the render function of a `<script setup>` SFC (it is
  // a closure over that component's own setup scope), so extending one renders
  // nothing and warns "Invalid vnode type". Wrapping works for both styles.
  const props = {
    ...data,
    modelValue: true,
    onClose: (_) => {
      close(el, popup);
      onClosed?.();
    }
  };
  const popup = createApp({ render: (_) => h(component, props) });
  popup.mount(el);
};

const popupInfo = (message, title, icon, color = '#140F2E', action) =>
  popup(ModalInfo, { message, title, icon, color, action });
const popupSuccess = (message, title = tolgee.t('popups.modal.success.title'), icon = 'done') =>
  popupInfo(message, title, icon, '#4da64d');
const popupError = (message, title = tolgee.t('popups.modal.error.title'), icon = 'cancel', action) => {
  popupInfo(message, title, icon, '#ff4d4d', action);
  const err = new Error(message);
  err.name = title;
  window?.faro?.api?.pushError(err);
};

const popupDictionary = (payload) => popup(ModalDictionary, payload);
// At most ONE notification modal, listing every popup the page load brought —
// never a modal per notification. Opening them one per row stacked their
// overlays to solid black; opening them one after another made the person
// dismiss a sequence before they could use the page. While one is open, a
// further response is ignored, and ids already shown are skipped so the count
// request an in-app navigation repeats — before the server has recorded them
// as shown — cannot bring the same ones back.
const shownNotificationIds = new Set();
let isNotificationOpen = false;

const popupNotifications = ({ notifications = [] }) => {
  if (isNotificationOpen) return;
  const fresh = notifications.filter((e) => !shownNotificationIds.has(String(e._id)));
  if (!fresh.length) return;
  fresh.forEach((e) => shownNotificationIds.add(String(e._id)));
  isNotificationOpen = true;
  popup(ModalNotification, { notifications: fresh }, () => {
    isNotificationOpen = false;
  });
};

const popupConfirm = function (
  title,
  message,
  labelConfirm = tolgee.t('popups.modal.confirm.yes'),
  labelDiscard = tolgee.t('popups.modal.confirm.cancel')
) {
  const el = document.createElement('div');
  document.body.appendChild(el);
  let popup = createApp(
    { extends: ModalConfirm },
    {
      modelValue: true,
      title,
      message,
      labelConfirm,
      labelDiscard,
      onClose: function () {
        close(el, popup);
      }
    }
  );
  const popupRoot = popup.mount(el);
  popupRoot.promise.then((res) => close(el, popup));
  return popupRoot.promise;
};

export default {
  popupInfo,
  popupSuccess,
  popupError,
  popupConfirm,
  popupNotifications,
  install: (app) => {
    app.component('vModal', VModal);
    app.config.globalProperties.$popupInfo = popupInfo;
    app.config.globalProperties.$popupSuccess = popupSuccess;
    app.config.globalProperties.$popupError = popupError;
    app.config.globalProperties.$popupDictionary = popupDictionary;
    app.config.globalProperties.$popupConfirm = popupConfirm;
  }
};
